import { useEffect, useState } from 'react';
// @ts-ignore: no def file
import { RouteComponentProps } from 'react-router';
import { Event } from '../../../../dorian-shared/types/event/Event';
import { DetailedPrize } from '../components/EventForm/DetailedPrizes/DetailedPrizeType';
import { EventFormValues, createInitialFormValues, eventToFormValues } from '../components/EventForm/EventForm';
import { FileInputValue } from '../components/ImageInput/ImageInput';

interface DuplicateLocationState {
  duplicateEvent?: Event;
  duplicatePrizes?: DetailedPrize[];
}

function extractFileNameFromUrl(url: string): string {
  try {
    const pathname = new URL(url).pathname;
    return pathname.split('/').pop() || 'image.jpg';
  } catch {
    return 'image.jpg';
  }
}

async function fetchImageAsFileInput(imageUrl: string): Promise<FileInputValue> {
  const response = await fetch(imageUrl);
  const blob = await response.blob();
  return {
    fileName: extractFileNameFromUrl(imageUrl),
    file: blob,
  };
}

export function useDuplicateFormValues(location: RouteComponentProps['location']) {
  const state = location.state as DuplicateLocationState | undefined;
  const isDuplicate = Boolean(state?.duplicateEvent);

  const [formValues, setFormValues] = useState<EventFormValues>(
    () => {
      if (!state?.duplicateEvent) return createInitialFormValues();
      const { id, ...eventWithoutId } = state.duplicateEvent;
      const prizes = (state.duplicatePrizes ?? []).map(({ id: _id, ...rest }) => rest);
      const values = eventToFormValues(eventWithoutId, prizes as DetailedPrize[]);
      // eventToFormValues sets ringColorTouched=false, which means "leave unchanged" on edit. A duplicate has no stored colour, so it must send the colour on the first Create.
      return {
        ...values,
        ringColorTouched: !!eventWithoutId.ringColor,
      };
    },
  );
  const [isPreparing, setIsPreparing] = useState(isDuplicate);

  useEffect(() => {
    if (!isDuplicate || !state?.duplicateEvent) return;

    let cancelled = false;
    const prizes = state.duplicatePrizes ?? [];

    const coverPromise = state.duplicateEvent.imageUrl
      ? fetchImageAsFileInput(state.duplicateEvent.imageUrl)
      : Promise.resolve(undefined);

    // Fetch the card icon and modal cover so the first Create uploads them; otherwise the form would show previews but send no file.
    const cardPromise = state.duplicateEvent.cardImageUrl
      ? fetchImageAsFileInput(state.duplicateEvent.cardImageUrl).catch(() => undefined)
      : Promise.resolve(undefined);

    const modalPromise = state.duplicateEvent.detailImageUrl
      ? fetchImageAsFileInput(state.duplicateEvent.detailImageUrl).catch(() => undefined)
      : Promise.resolve(undefined);

    const prizePromises = prizes.map((prize) => (
      prize.imageUrl
        ? fetchImageAsFileInput(prize.imageUrl).catch(() => undefined)
        : Promise.resolve(undefined)
    ));

    Promise.all([coverPromise, cardPromise, modalPromise, ...prizePromises])
      .then(([coverImage, cardImage, modalImage, ...prizeImages]) => {
        if (cancelled) return;
        // This write bypasses the removal/upload mutual-exclusion handlers in EventForm. It is safe only because NewEventItemPage renders the form behind isPreparing, so the operator cannot click Remove until this has landed.
        setFormValues((prev) => ({
          ...prev,
          newCoverImage: coverImage,
          newCardImage: cardImage,
          newModalImage: modalImage,
          detailedEventPrizes: prev.detailedEventPrizes.map((prize, i) => ({
            ...prize,
            image: prizeImages[i],
          })),
        }));
      })
      .catch(() => { /* user can upload manually */ })
      .finally(() => {
        if (!cancelled) setIsPreparing(false);
      });

    return () => { cancelled = true; };
  }, [isDuplicate, state]);

  return { formValues, isPreparing };
}
